/**
 * THE generic question renderer. One `QuestionDef` in, the right control out —
 * for all 115 questions in specification §3, from catalog data alone.
 *
 * Every section screen is built from this, so it can hold no per-question
 * knowledge beyond what the catalog carries. If a question needs special
 * handling, the fix is a field on `QuestionDef`, not a branch on its id.
 *
 * ── What this component is not allowed to do ─────────────────────────────────
 *
 *  - It does not decide whether a question is ASKED. That is `isVisible()`, and
 *    `QuestionList` applies it (via `visibleQuestions`) before this ever runs.
 *  - It does not COMPUTE a quantity, and since D-033 it does not show one
 *    either: `computeDerived()`'s lines travel with the record and have no
 *    surface on a card.
 *  - It does not compose validation text. The chip under a question is
 *    `ValidationIssue.message`, rendered by `QuestionList`.
 *  - It does not decide which OPTIONS a question offers. `visibleOptions`
 *    (D-007) narrows by form set and is the fallback here; a per-option gate
 *    (**D-024** — F8's Shunt box exists only in a sprinklered machine room)
 *    needs the document to evaluate, which this component does not have and
 *    must not acquire. `QuestionList` passes the narrowed list in through
 *    `options`, from `optionsFor(question, doc, scope)`.
 *
 * Presentational: no store, no `useSurvey()`, no engine calls. Values come in,
 * an `AnswerValue` goes out, and the caller persists it. That is what lets the
 * whole thing be unit-tested with `renderToStaticMarkup` in the node suite.
 *
 * ── Defaults are WRITTEN now, and not here (D-015 → D-016) ───────────────────
 * Every Yes/No question — the sixteen `flagCount`s and the fifty-odd plain
 * `bool`s — opens already reading No, so a surveyor can walk a section touching
 * only what applies. Round 2 did that by DISPLAY alone and round 3 found the
 * hole: the control said No while the document said nothing, so a section walked
 * without a tap reached the office as a wall of unanswered questions. The
 * default is a real answer now, planned by `src/ui/state/answerDefaults.ts` and
 * applied by `QuestionList`.
 *
 * What stays here is the honesty. A control showing an answer NOBODY GAVE is
 * wrapped in `UntouchedMark`, which carries `data-default` so that "No because
 * the surveyor said No" is never mistaken for "No because the form opened on
 * No". This component remains presentational: it is TOLD which it is, through
 * the `defaulted` prop, and has no way to look it up.
 *
 * ── The two flavours of AUTO, and neither has a surface (D-033, R14) ────────
 * `catalog.ts` documents the split. A FULLY DERIVED question
 * (`DERIVED_ONLY_QUESTION_IDS`: B14, B17, E4b, E4c, E6a, E9d, F1, L1) renders
 * NOTHING — valued or not. D-029 R3 made the empty calc invisible and left the
 * valued one as a chip; the design authority, 2026-09-01: *"Delete any card
 * that only contains helper text, such as 'New LED Strip Fixtures - 1 fixture
 * - 1 in place now, none to add' or '3 ea - one per car in the room.' This is
 * just noise."* An AUTO QUANTITY off a surveyor input (E6, E8, L8's Y/N; E7c,
 * H3's measurement) keeps its control and loses the chip beside it — *"any
 * informational text or calculated fields that do not provide value or
 * require a surveyor's answer."* The values still compute (`computeDerived`)
 * and still travel with the submission; only their surface is gone.
 *
 * ── No ⓘ, no ✎ (D-033, R13) ─────────────────────────────────────────────────
 * *"Removing pencil and information icons from every question card, as they
 * clutter the screen."* The catalog hint keeps its string — rule zero — and
 * has no surface anywhere. The ✎'s work is `QuestionList`'s business.
 */

import type { ReactNode } from 'react';

import { DERIVED_ONLY_QUESTION_IDS, visibleOptions } from '../../core/catalog';
import type {
  AnswerValue,
  Contact,
  FlagCount,
  FormSet,
  Measure,
  QuestionDef,
  QuestionOption,
  WallRange,
} from '../../core/types';
import { ContactField } from './ContactField';
import { DateField } from './DateField';
import { MeasureField } from './MeasureField';
import { RadioList } from './RadioList';
import { RangeField } from './RangeField';
import { Segmented } from './Segmented';
import { Stepper } from './Stepper';
import { TextField } from './TextField';
import { YesNo } from './YesNo';

/**
 * `measure` covers two shapes and `QuestionDef` carries no dimensionality flag,
 * so the one two-dimensional question in §3 is named here. H2 is the mini-split
 * room (length × width, spec Q20–21); E7c and H3 are single-dimension runs.
 * Reported to the design authority as a resolved ambiguity — the durable fix is
 * a `measureMode` field on the catalog entry.
 */
const AREA_MEASURE_QUESTION_IDS: readonly string[] = ['H2'];

/**
 * `text` questions the surveyor is expected to WRITE INTO rather than fill in.
 * Both are §4 rule 10 lines — priced by hand at the office off exactly this
 * text — so both get a textarea: H4 (chilled water) and, since Amendment 5, F2
 * (what the FLS subcontractor should know). Same ambiguity class as above.
 */
/** The keyboard iOS raises for a text question that is really digits (D-051: ZIP). */
const INPUT_MODE_BY_ID: Partial<Record<string, 'numeric' | 'tel' | 'decimal'>> = {
  B2c: 'numeric',
};

const MULTILINE_QUESTION_IDS: readonly string[] = ['H4', 'F2'];

/**
 * `ranges` questions whose rows are LANDINGS AND NOTHING ELSE.
 *
 * Range entry has two jobs since Amendment 5 and only one of them is about a
 * material. L4a's rows carry a wall type because the material selects the
 * per-landing cut-and-patch price; F11a (landings missing a smoke detector) and
 * F12a (landings that are sprinklered) are spans of landings for the fire alarm
 * company, and there is nothing to pick. `WallRange.wallType` is optional for
 * exactly this reason (types.ts), and putting a material picker on these rows
 * would either invent survey data or force the surveyor to dismiss a control
 * that means nothing.
 *
 * Named here rather than inferred, and pinned by a test: `fls-ui.test.ts`
 * asserts every `ranges` question in the catalog is in exactly one of the two
 * buckets, so a sixteenth one cannot quietly get the wrong control.
 */
const LANDINGS_ONLY_RANGE_QUESTION_IDS: readonly string[] = ['F11a', 'F12a'];

/**
 * Kinds whose control renders its own `<label>`. For those the question prompt
 * IS that label — rendering a heading as well would read the prompt twice to a
 * screen reader.
 */
const SELF_LABELLING: readonly QuestionDef['kind'][] = ['text', 'date', 'measure', 'contact'];

export interface QuestionFieldProps {
  question: QuestionDef;
  /**
   * The room's question set, for `visibleOptions` (D-007). Building and ad hoc
   * questions have no room; pass either set — no option there is tagged.
   */
  formSet: FormSet;
  /**
   * The choices to offer, already narrowed for HERE — **D-024**.
   *
   * Omit it and the form-set filter alone applies, which DROPS any option
   * carrying its own gate. That is the safe direction and the same rule both
   * engines use for a question's gates: an unanswered gate is a closed gate,
   * and showing F8's Shunt box in a dry machine room would invite a tick
   * asserting a relay nobody could have seen. A connected caller passes
   * `optionsFor(question, doc, scope)`.
   */
  options?: readonly QuestionOption[];
  /** The stored answer, or undefined while unanswered. */
  value: AnswerValue | undefined;
  /**
   * True when `value` is the form's own opening default rather than the
   * surveyor's answer — **D-016**. Presentational only: it changes nothing that
   * is rendered, and adds the `data-default` marker that says *"pressed, but
   * nobody has said so"*. The caller reads it from
   * `SurveyDocument.defaultedAnswers`; this component holds no state and cannot.
   */
  defaulted?: boolean;
  /**
   * Persist it. The caller writes to IndexedDB; this component never does.
   * Returning `false` says a guard refused the write — passed through to a
   * `number` question's Stepper so it can forget the tap it just sent.
   */
  onChange: (next: AnswerValue) => boolean | void;
  /** Ceiling for the L4a landing ranges — the group's L1 derived quantity. */
  maxLanding?: number;
  disabled?: boolean;
}

export function QuestionField({
  question,
  formSet,
  options,
  value,
  onChange,
  maxLanding,
  defaulted = false,
  disabled = false,
}: QuestionFieldProps): ReactNode {
  /* ── fully derived: no surface, valued or not (D-033 R14) ───────────────── */
  if (DERIVED_ONLY_QUESTION_IDS.includes(question.id)) return null;

  // A self-labelling control carries the prompt in its own <label>; the extra
  // class promotes that label from a micro-caption to a question prompt so the
  // list keeps one visual rhythm.
  const needsHeading = !SELF_LABELLING.includes(question.kind);

  return (
    <div className={needsHeading ? 'qfield' : 'qfield qfield--selflabel'}>
      {needsHeading ? <Heading question={question} /> : null}
      <Control
        question={question}
        formSet={formSet}
        options={options ?? visibleOptions(question, formSet)}
        value={value}
        onChange={onChange}
        maxLanding={maxLanding}
        defaulted={defaulted}
        disabled={disabled}
      />
    </div>
  );
}

/**
 * The prompt, VERBATIM from the catalog, and nothing beside it (D-033 R13).
 *
 * The label is never touched: it is written to be read aloud and D-015 depends
 * on its exact yes/no phrasing. The catalog hint is the same string it always
 * was (rule zero); it has no surface on any card.
 */
function Heading({ question }: { question: QuestionDef }): ReactNode {
  return (
    <div className="q qhead">
      <p className="q__label" data-legibility="question">
        {question.label}
      </p>
    </div>
  );
}

/* ═════════════════════════════ the controls ══════════════════════════════ */

interface ControlProps {
  question: QuestionDef;
  formSet: FormSet;
  /** Already narrowed — form set, and any per-option gate. Never re-filtered. */
  options: readonly QuestionOption[];
  value: AnswerValue | undefined;
  onChange: (next: AnswerValue) => boolean | void;
  maxLanding?: number;
  /** The value on screen is the form's opening answer. See `UntouchedMark`. */
  defaulted: boolean;
  disabled: boolean;
}

function Control({
  question,
  formSet,
  options,
  value,
  onChange,
  maxLanding,
  defaulted,
  disabled,
}: ControlProps): ReactNode {
  switch (question.kind) {
    case 'text':
      return (
        <TextField
          label={question.label}
          value={value?.kind === 'text' ? value.value : ''}
          onChange={(text) => onChange({ kind: 'text', value: text })}
          multiline={MULTILINE_QUESTION_IDS.includes(question.id)}
          inputMode={INPUT_MODE_BY_ID[question.id] ?? 'text'}
          disabled={disabled}
        />
      );

    case 'number': {
      const stored = value?.kind === 'number' ? value.value : undefined;
      return (
        <div className="qfield__inline">
          {question.uom ? <span className="qfield__uom">{question.uom}</span> : null}
          <Stepper
            // D-055: an unanswered count with a floor of 1 (G9a, G14a, A3a, A2)
            // is blank (undefined), not a "0" it cannot produce. Still a gap —
            // nothing is written until the surveyor types or taps +. A floor
            // of 0 (L3a, B11…) keeps D-053's readout of 0.
            value={stored ?? ((question.min ?? 0) >= 1 ? undefined : 0)}
            onChange={(next) => onChange({ kind: 'number', value: next })}
            // D-054: the catalog's floor and cap (B12 ≤ 10; G9a, G14a, A3a ≥ 1).
            // A typed number outside them is clamped in the box, visibly.
            min={question.min}
            max={question.max}
            ariaLabel={question.label}
            disabled={disabled}
          />
        </div>
      );
    }

    case 'bool':
      // D-016: opens on No, and says in the DOM that the No is the form's and
      // not the surveyor's. Same ATTRIBUTE as a flagCount's Y/N — one rule for
      // anything walking the page — but its own class: `.qfield__yn` means "a
      // flagCount's yes/no half" to the audits that read prompts off it, and a
      // plain bool's prompt is not held to the same wording.
      return (
        <UntouchedMark untouched={defaulted} shows="no" className="qfield__bool">
          <YesNo
            value={value?.kind === 'bool' ? value.value : undefined}
            onChange={(next) => onChange({ kind: 'bool', value: next })}
            ariaLabel={question.label}
            disabled={disabled}
          />
        </UntouchedMark>
      );

    case 'tristate':
      // L9c. "Unknown" is a real answer, not a missing one — it routes the
      // question to the office instead of making the surveyor guess (Q31), and
      // it is what this question OPENS on: recording "no" would turn "nobody
      // asked the facility" into "the facility said no". See defaultAnswerFor.
      return (
        <UntouchedMark untouched={defaulted} shows="unknown" className="qfield__tri">
          <Segmented
            options={[
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' },
              { value: 'unknown', label: 'Unknown' },
            ]}
            value={value?.kind === 'tristate' ? value.value : undefined}
            onChange={(next) => onChange({ kind: 'tristate', value: next })}
            ariaLabel={question.label}
            disabled={disabled}
          />
        </UntouchedMark>
      );

    case 'select':
      return (
        <RadioList
          options={options}
          value={value?.kind === 'select' ? value.value : undefined}
          onChange={(next) => onChange({ kind: 'select', value: next })}
          ariaLabel={question.label}
          disabled={disabled}
        />
      );

    case 'multiselect':
      return (
        <MultiSelect
          options={options}
          value={value?.kind === 'multiselect' ? value.value : []}
          onChange={(next) => onChange({ kind: 'multiselect', value: next })}
          ariaLabel={question.label}
          disabled={disabled}
        />
      );

    case 'contact':
      return (
        <ContactField
          label={question.label}
          value={value?.kind === 'contact' ? value.value : undefined}
          onChange={(next: Contact) => onChange({ kind: 'contact', value: next })}
          disabled={disabled}
        />
      );

    case 'date':
      return (
        <DateField
          label={question.label}
          value={value?.kind === 'date' ? value.value : ''}
          onChange={(next) => onChange({ kind: 'date', value: next })}
          disabled={disabled}
        />
      );

    case 'measure':
      return (
        <MeasureField
          label={question.label}
          value={value?.kind === 'measure' ? value.value : undefined}
          onChange={(next: Measure) => onChange({ kind: 'measure', value: next })}
          mode={AREA_MEASURE_QUESTION_IDS.includes(question.id) ? 'area' : 'run'}
          // The surveyor always paces FEET. `question.uom` is what the office
          // PRICES (a 25-ft unit), which is the calc engine's business.
          unit="ft"
          disabled={disabled}
        />
      );

    case 'ranges':
      return (
        <RangeField
          value={value?.kind === 'ranges' ? value.value : []}
          onChange={(next: WallRange[]) => onChange({ kind: 'ranges', value: next })}
          formSet={formSet}
          ariaLabel={question.label}
          disabled={disabled}
          landingsOnly={LANDINGS_ONLY_RANGE_QUESTION_IDS.includes(question.id)}
          {...(maxLanding ? { maxLanding } : {})}
        />
      );

    case 'flagCount':
      return (
        <FlagCountControl
          question={question}
          value={value?.kind === 'flagCount' ? value.value : undefined}
          onChange={(next) => onChange({ kind: 'flagCount', value: next })}
          // A flagCount DISPLAYS No when it holds nothing at all, so an
          // unanswered one is marked too. A `bool` and a `tristate` display
          // nothing pressed in that state, so for those the mark would be a
          // claim about a control that is making no claim.
          untouched={defaulted || value === undefined}
          disabled={disabled}
        />
      );
  }
}

/* ─────────────────── "pressed, but nobody has said so" ─────────────────── */

/**
 * The wrapper that marks a control showing an answer NOBODY GAVE — D-016.
 *
 * The distinction is not cosmetic and it is not only for tests. "No because the
 * surveyor said so" and "No because the form opened on No" are different claims
 * about the same document, and three things depend on being able to tell them
 * apart: the D-014/D-015 timing gate (`validationTiming.ts`), which may not call
 * a disconnect split wrong until the surveyor has engaged with both halves; the
 * offline walk-and-submit driver, which would otherwise walk straight past every
 * defaulted question and quietly test that the walk skipped them; and anyone
 * reading the page to find out what has actually been asked.
 *
 * Renders a plain wrapper and nothing else — no class of its own beyond the one
 * asked for, no text, no styling. `.qfield__yn` carries no CSS; it exists to be
 * selected.
 */
export function UntouchedMark({
  untouched,
  shows,
  className = 'qfield__yn',
  children,
}: {
  untouched: boolean;
  /** What the control is displaying while untouched: "no", "unknown". */
  shows: string;
  className?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div className={className} {...(untouched ? { [FLAG_COUNT_UNTOUCHED_ATTR]: shows } : {})}>
      {children}
    </div>
  );
}

/* ─────────────────────────── flagCount: Y/N + count ───────────────────────── */

interface FlagCountControlProps {
  question: QuestionDef;
  value: FlagCount | undefined;
  onChange: (next: FlagCount) => void;
  untouched: boolean;
  disabled: boolean;
}

/**
 * The value written when the Y/N of a `flagCount` question is tapped.
 *
 * Toggling the flag off ZEROES the count rather than merely hiding it.
 * `FlagCountSchema` refuses to store a count alongside `flag:false` (the
 * invariant is stated in types.ts and enforced in schema.ts), so hiding the
 * stepper while leaving the number behind would produce a document the store
 * rejects at the wire boundary. Exported so the rule has a unit test that does
 * not need a click.
 */
export function nextFlagCount(current: FlagCount | undefined, flag: boolean): FlagCount {
  return { flag, count: flag ? current?.count ?? 0 : 0 };
}

/**
 * What a `flagCount` question SHOWS before it has been touched — **D-015**.
 *
 * The design authority, 2026-08-06: *"Set the default answer for every question
 * to No or None, so users aren't forced to fill out unnecessary fields… the
 * default quantity is 0 for each question until the user specifies otherwise."*
 *
 * So the Y/N opens on **No** and the quantity on **0**, and a surveyor can walk a
 * whole section touching only what applies. This is exactly what every engine
 * already computes for an unanswered `flagCount` — `countOf()` reads it as 0 and
 * no `showWhen` in §3 gates on `flagIs: false` — so the screen is now saying out
 * loud what the arithmetic was silently assuming. It is a DISPLAY default, not a
 * write: see `FLAG_COUNT_UNTOUCHED_ATTR`.
 */
export const FLAG_COUNT_DEFAULT: FlagCount = { flag: false, count: 0 };

/**
 * Marks a `flagCount` Y/N that is showing `FLAG_COUNT_DEFAULT` rather than a
 * stored answer.
 *
 * The distinction is not cosmetic and it is not only for tests. "No, because the
 * surveyor said so" and "No, because nothing has been said yet" are different
 * documents: the second is still an unanswered required question, still a gap at
 * submit (§4 rule 11), and — the case this attribute was actually added for —
 * still not enough engagement to call a disconnect split WRONG (D-015 §3, and
 * `settledFor()` in src/ui/state/validationTiming.ts). Anything walking the DOM
 * and treating a pressed button as an answer would silently skip these; the
 * offline walk-and-submit driver reads this attribute so it cannot.
 */
export const FLAG_COUNT_UNTOUCHED_ATTR = 'data-default';

/**
 * The "Y/N + count" shape used by sixteen §3 questions.
 *
 * The count control appears ONLY once the flag is yes, and it appears with its
 * own prompt — "How many?" — above it at full question size (D-011 item 6). A
 * bare stepper under a Yes is a control with no question attached to it; under a
 * No it could only ever show the 0 the schema requires anyway.
 */
function FlagCountControl({
  question,
  value,
  onChange,
  untouched,
  disabled,
}: FlagCountControlProps): ReactNode {
  const flag = value?.flag ?? FLAG_COUNT_DEFAULT.flag;
  const count = value?.count ?? FLAG_COUNT_DEFAULT.count;

  return (
    <>
      <UntouchedMark untouched={untouched} shows="no">
        <YesNo
          value={flag}
          onChange={(yes) => onChange(nextFlagCount(value, yes))}
          ariaLabel={question.label}
          disabled={disabled}
        />
      </UntouchedMark>
      {flag ? (
        <div className="qfield__followup qfield__inline--reveal">
          {/* The prompt the old label used to carry, back where it belongs: above
           * the control, at question size, asked only once it applies. */}
          <p className="q q--followup" data-legibility="question">
            How many?
          </p>
          <div className="qfield__inline">
            <span className="qfield__uom">{question.uom ?? 'ea'}</span>
            <Stepper
              value={count}
              onChange={(next) => onChange({ flag: true, count: next })}
              ariaLabel={`${question.label} — how many`}
              disabled={disabled}
              // D-053: this box goes away when No is tapped, and writing its
              // draft on the way out would be `flag: true` again.
              commitDraftOnUnmount={false}
            />
          </div>
        </div>
      ) : null}
    </>
  );
}

/* ────────────────────────────── multiselect ──────────────────────────────── */

interface MultiSelectProps {
  options: readonly { value: string; label: string; hint?: string }[];
  value: readonly string[];
  onChange: (next: string[]) => void;
  ariaLabel: string;
  disabled: boolean;
}

/**
 * Toggle buttons rather than checkboxes: a 44px target with the label inside it
 * beats a 16px box the thumb has to find. `aria-pressed` carries the state.
 *
 * The §3 multiselects (M3/M6/M7) are STRUCTURAL and are captured by the room
 * setup builder, not here — this exists so the renderer is total over
 * `AnswerKind` rather than throwing if the catalog ever grows one.
 */
function MultiSelect({
  options,
  value,
  onChange,
  ariaLabel,
  disabled,
}: MultiSelectProps): ReactNode {
  const chosen = new Set(value);
  return (
    <div className="multiselect" role="group" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="multiselect__opt"
          aria-pressed={chosen.has(option.value)}
          disabled={disabled}
          onClick={() =>
            onChange(
              chosen.has(option.value)
                ? value.filter((v) => v !== option.value)
                : [...value, option.value],
            )
          }
        >
          {option.label}
          {option.hint ? <small data-legibility="helper">{option.hint}</small> : null}
        </button>
      ))}
    </div>
  );
}
